import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRightSmall } from '@peacecover/icon';
import { useGetSceneSubscriptions } from '../../../domains/scene/ui/hooks/useGetSceneSubscriptions';
import EmptyCase from '../../../components/errors/EmptyCase';
import { ROUTES } from '../../../constants/routes';
import type { components } from '../../../types/schema';

type SceneSubscription = components['schemas']['ApiV1UserSceneSubscription'];

const PreviewSubscriptionList = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useGetSceneSubscriptions();

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(['sceneSubscriptions']);
    };
  }, [queryClient]);
  
  
  const previewList = data?.pages?.flatMap(
    (page) =>
      page.userSceneSubscriptions?.filter(
        // @ts-ignore
        (subscription) => subscription.contentType === 'SceneEpisode'
      ) ?? []
  );

  const loadMore = () => {
    if (isFetching || !hasNextPage) return;
    fetchNextPage();
  };

  if (isLoading) {
    return null;
  }

  if (data?.pages?.length && previewList?.length === 0) {
    return (
      // @ts-ignore
      <EmptyCase emoji="😳" title={t('SceneMain.subscription.emptyTitle')} />
    );
  }

  return (
    <div className="h-full">
      <Virtuoso
        overscan={480}
        defaultItemHeight={260}
        style={{ height: '100%' }}
        data={previewList}
        endReached={loadMore}
        atBottomThreshold={200}
        itemContent={(_index, episode: SceneSubscription) => (
          <SceneOriginalEpisodeItem episode={episode} />
        )}
      />
    </div>
  );
};

export default PreviewSubscriptionList;

const SceneOriginalEpisodeItem = ({ episode }: { episode: SceneSubscription }) => {
  const navigate = useNavigate();
  const handleNavigateToEpisode = () => {
    const baseParams = new URLSearchParams();
    if (episode.locator?.sceneEpisodeId)
      baseParams.set('episodeId', episode.locator.sceneEpisodeId);
    if (episode.locator?.paragraphIndex != null)
      baseParams.set('cursor', String(episode.locator.paragraphIndex));
    if (episode.locator?.charOffset != null)
      baseParams.set('charOffset', String(episode.locator.charOffset));

    const queryString = baseParams.toString();
    navigate(
      `${ROUTES.SCENE.ORIGINAL_EPISODES.GO(episode.sceneId ?? '')}${queryString ? `?${queryString}` : ''}`
    );
  };

  return (
    <div className="mx-[24px] py-[20px] flex items-center justify-between border-b border-separator-dark-2">
      <div className="flex items-center gap-[14px]">
        <div className="w-[69px] h-[109px] rounded-[5px] border border-pure-black bg-blue-300" />
        <div className="flex flex-col items-start gap-[8px]">
          <p className="text-[19px] font-semibold leading-[23px] text-[#FFF]">제목</p>
          <p className="text-[12px] font-medium leading-[14px] text-[rgba(255,255,255,0.80)]">
            by 작가 · 글자수
          </p>
          <div className="flex items-center gap-[8px]">
            <div className="w-[145px] h-[6px] bg-white-opacity-200 rounded-[20px]">
              <div
                className="h-[6px] bg-[linear-gradient(90deg,#41F7A2_0%,#5C3DFA_100%)] rounded-[20px]"
                style={{ width: '60%' }}
              />
            </div>
            <p className="text-[12px] font-medium leading-[14px] text-[rgba(255,255,255,0.80)]">
              60%
            </p>
          </div>
        </div>
      </div>
      <button type="button" onClick={handleNavigateToEpisode}>
        <ArrowRightSmall className="text-pure-white" />
      </button>
    </div>
  );
};
